//src/components/RecentDeletedFiles

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDarkMode } from "../../context/darkModeContext";
import { Dialog, DialogContent, Skeleton, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useFolderStore } from "../../store/folderStore";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DeleteRestoreFile from "./DeleteRestoreFile";
import FileBg from "../../generators/fileBGGenerator";
import DoneIcon from "@mui/icons-material/Done";
import { useDepartment } from "../../stores/departmentStore";
import fileContext from "../../context/fileContext";
import { api } from "../../utils/axios-instance";
import { deleteObject, getStorage, ref } from "firebase/storage";
import secureLocalStorage from "react-secure-storage";
import { fileIcons } from "../../utils/icon-mimetypes";

// images from public folder
const threeDots = `${process.env.PUBLIC_URL}/assets/threedots.webp`;
const DefaultIcon = `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;
const Danger = `${process.env.PUBLIC_URL}/assets/danger.webp`;

const RecentDeletedFiles = ({ recycleFiles, loading, removeFile }) => {
  const { darkMode } = useDarkMode();

  const [displayFiles, setDisplayFiles] = useState([]);
  const departments = useDepartment((state) => state.departments);
  const recycleFolders = useFolderStore((state) => state.recycleFolders);
  const context = useContext(fileContext);
  const { anchorEl, setAnchorEl, setRefreshRecycleBin } = context;
  const [menuFile, setMenuFile] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [delLoading, setDelLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [select, setSelect] = useState(false);
  const open = Boolean(anchorEl && document.body.contains(anchorEl));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    if (anchorEl && !document.body.contains(anchorEl)) {
      setAnchorEl(null);
    }
  }, [displayFiles]);

  const handleDelete = async (file) => {
    const profileData = JSON.parse(secureLocalStorage.getItem("profileData"));

    // UI-side permission check (backend will enforce too)
    if (
      !(
        profileData?.id === file.owner || profileData?.role_priv === "org_admin"
      )
    ) {
      throw new Error("You are not the owner of the file.");
    }

    const storage = getStorage();
    const fileId = file.fileId || file.id;

    try {
      // Permanent delete = ONLY delete-file (NO restore-file call)
      await api.delete(`/file/delete-file/${fileId}/`);

      // Delete blob from Firebase storage (best-effort; don't fail delete if it errors)
      try {
        const orgKey = profileData.org_idId || profileData.org_id; // supports either shape
        const fileRef = ref(storage, `files/${orgKey}/${fileId}`);
        await deleteObject(fileRef);
      } catch (e) {
        console.warn("Firebase storage delete failed (ignored):", e?.message);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Unknown error";
      throw new Error("Error occurred while deleting the file: " + msg);
    }
  };

  const deleteFiles = async () => {
    setDelLoading(true);

    // CLOSE MENU BEFORE DELETING
    setAnchorEl(null);

    try {
      const deletePromises = selectedFiles?.map((file) => handleDelete(file));
      await Promise.all(deletePromises);

      // remove deleted files from UI immediately
      setDisplayFiles((prev) =>
        prev.filter(
          (f) =>
            !selectedFiles.some(
              (sf) => (sf.fileId || sf.id) === (f.fileId || f.id)
            )
        )
      );

      setSnackbarSeverity("success");
      setSnackbarMessage("Files deleted successfully.");
      setSnackbarOpen(true);

      setSelectedFiles([]);

      setTimeout(() => {
        setSelect(false);
        setOpenConfirmation(false);
        setRefreshRecycleBin(true);
      }, 500);
    } catch (error) {
      console.error(error);
      setSnackbarSeverity("error");
      setRefreshRecycleBin(true);
      setSnackbarMessage(error.message || "Error deleting files.");
      setSnackbarOpen(true);
    } finally {
      setDelLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMultiFileSelect = (file) => {
    if (selectedFiles.includes(file)) {
      console.log("removing file");
      const filteredFiles = selectedFiles.filter(
        (selectedFile) => selectedFile.id !== file.id
      );
      setSelectedFiles(filteredFiles);
    } else {
      console.log("adding file");
      setSelectedFiles([...selectedFiles, file]);
    }
  };
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getIconByMimeType = (mimeType) => {
    return fileIcons[mimeType] || DefaultIcon;
  };

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes == null) return "";
    if (typeof sizeInBytes === "string") return sizeInBytes; // already formatted

    const units = ["B", "KB", "MB", "GB"];
    let size = Number(sizeInBytes);
    if (Number.isNaN(size)) return "";

    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  useEffect(() => {
    let cancelled = false;

    const enrich = async () => {
      if (!Array.isArray(recycleFiles) || recycleFiles.length === 0) {
        setDisplayFiles([]);
        return;
      }

      try {
        const enriched = await Promise.all(
          recycleFiles.map(async (rb) => {
            const fileId = rb.fileId || rb.id;
            if (!fileId) return rb;

            // If backend already sent rich data, don't refetch.
            if (
              rb.fileName &&
              (rb.mimeType || rb.mimetype) &&
              (rb.fileSize || rb.size)
            ) {
              return {
                ...rb,
                fileId,
                fileName: rb.fileName,
                mimeType: rb.mimeType || rb.mimetype,
                fileSize: rb.fileSize || rb.size,
              };
            }

            try {
              const info = await api.get(`/file/sharedFileInfo/${fileId}/`);
              const d = info?.data || {};
              const meta = d.metadata || {};

              const matchedDepartment = departments.find(
                (dept) => dept.id === d.department_ids?.[0]
              );

              return {
                ...rb,
                fileId,
                folder_id: rb.folder_id || d.folder_id,
                
                fileName: rb.fileName || d.name,
                mimeType: rb.mimeType || meta.mimetype || d.mimetype,
                fileSize: rb.fileSize || meta.size || rb.size,
                profilePic: rb.profilePic || d.profile_pic || d.profilePic,
                //

                color:
                  rb.color ||
                  d.color ||
                  matchedDepartment?.metadata?.bg ||
                  "#d3d3d3",
                versions: rb.versions || (d.versioning || {}).versions,
                currVersion:
                  rb.currVersion || (d.versioning || {}).curr_version,
              };
            } catch (e) {
              return { ...rb, fileId };
            }
          })
        );

        if (!cancelled) setDisplayFiles(enriched);
      } catch {
        if (!cancelled) setDisplayFiles(recycleFiles);
      }
    };

    enrich();
    return () => {
      cancelled = true;
    };
  }, [recycleFiles, departments]);

  const filteredFiles = useMemo(() => {
    const deletedFolderIds = new Set(
      (recycleFolders || []).map((folder) => folder.id)
    );

    return displayFiles.filter((file) => {
      const folderId = file.folder_id || file.folderId;

      // Standalone deleted files should still appear
      if (!folderId) {
        return true;
      }

      // If its parent folder is already shown above,
      // don't show the file again.
      return !deletedFolderIds.has(folderId);
    });
  }, [displayFiles, recycleFolders]);

  const hexToRgba = (hex, alpha) => {
    let r = 0,
      g = 0,
      b = 0;

    if (!hex) return `rgba(0,0,0,${alpha})`;

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-row justify-end items-center gap-4">
        {!openConfirmation && select && (
          <button
            className="text-white px-6 py-1.5 rounded-md text-center bg-blue-700"
            onClick={() => {
              setSelect(false);
              setSelectedFiles([]);
            }}
          >
            Cancel
          </button>
        )}
        {select &&
          selectedFiles.length > 0 &&
          selectedFiles.length !== filteredFiles.length && (
            <button
              className="text-white px-6 py-1.5 rounded-md text-center bg-red-500"
              onClick={() => {
                setOpenConfirmation(true);
              }}
            >
              Delete selected
            </button>
          )}
        <button
          className="text-white px-6 py-1.5 rounded-md text-center bg-red-500"
          onClick={() => {
            setSelect(true);
            setSelectedFiles(filteredFiles);
            setOpenConfirmation(true);
          }}
        >
          Delete All
        </button>
        <button
          className="text-white px-6 py-1.5 rounded-md text-center bg-blue-700"
          onClick={() => setSelect(true)}
        >
          Select Files
        </button>
      </div>
      <div
        className={` grid grid-cols-5 gap-4 ${
          darkMode ? "text-gray-200" : "text-gray-600"
        }`}
      >
        {loading
          ? Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="border border-gray-200 p-3 rounded-2xl shadow-sm"
              >
                <span className="flex flex-row justify-between items-center px-1 my-1">
                  <Skeleton variant="circular" height={22} width={22} />
                  <img src={threeDots} alt="..." height={25} width={25} />
                </span>

                <span className="">
                  <Skeleton variant="rounded" height={60} width="100%" />
                  <span className="font-semibold">
                    <Skeleton height={22} width="100%" />
                  </span>
                </span>

                <span>
                  <span className="flex flex-row justify-between items-center my-1 px-1">
                    <span className="w-full text-sm text-gray-500 font-light">
                      <Skeleton height={18} width="70%" />
                    </span>
                    <Skeleton variant="circular" height={22} width={22} />
                  </span>
                </span>
              </div>
            ))
          : filteredFiles?.map((file, index) => {
              const color = file.color || "#3498db";
              const backgroundColor = hexToRgba(color, 0.1);
              return (
                <div
                  key={index}
                  className={`border border-gray-200 p-3 rounded-[16px] cursor-pointer relative`}
                  style={{
                    // backgroundColor: file.color || `#d3d3d3`,
                    backgroundColor,
                    backgroundImage: FileBg({
                      gradientColors: [
                        file.color,
                        file.color,
                        file.color,
                        file.color,
                      ],
                    }),
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    backgroundBlendMode: "lighten",
                  }}
                  onClick={() => handleMultiFileSelect(file)}
                >
                  <div
                    className="w-full h-full absolute left-0 top-0 bg-red-500 items-center justify-center rounded-[16px]"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      display:
                        selectedFiles.includes(file) && select
                          ? "flex"
                          : "none",
                    }}
                  >
                    <div className="py-3 px-3 rounded-full bg-green-400">
                      <DoneIcon />
                    </div>
                  </div>
                  <span className="flex justify-end items-center">
                    <span style={{ fontSize: 20 }} className="flex">
                      <button
                        className=""
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMenuClick(event);
                          setMenuFile(file);
                        }}
                      >
                        <img src={threeDots} height={25} width={25} alt="" />
                      </button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                        PaperProps={{
                          style: {
                            border: "1px solid [#111  82626]",
                            boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.1)",
                            borderRadius: "6px",
                          },
                        }}
                      >
                        <MenuItem
                          style={{ padding: "0px 10px", color: "#D1293D" }}
                        >
                          <DeleteRestoreFile //calling the recycle bin instead of directly deleting
                            fileName={menuFile.fileName}
                            owner={menuFile.owner}
                            id={menuFile.fileId || menuFile.id}
                            remove={removeFile}
                            currVersion={menuFile?.currVersion}
                            versions={menuFile?.versions}
                          />
                        </MenuItem>
                      </Menu>
                    </span>
                  </span>

                  <div>
                    <span className="flex justify-center items-center">
                      <img
                        src={getIconByMimeType(file.mimeType || file.mimetype)}
                        alt="File Preview"
                        className="rounded-md my-4"
                      />
                    </span>
                    <span>
                      <h5 className="font-semibold line-clamp-1 text-gray-700 text-sm mb-1">
                        {(file.fileName || file.name || "")?.split("_TS=")[0]}
                      </h5>
                      <span className="flex flex-row justify-between items-center">
                        <span>
                          <h6 className="font-semibold">
                            <span className="text-xs font-bold text-[10px] text-[#676767]">
                              {formatFileSize(file.fileSize || file.size)}
                            </span>
                          </h6>
                        </span>

                        <Avatar
                          src={file.profilePic}
                          alt="owner pic"
                          sx={{
                            width: 24,
                            height: 24,
                            border: "1px solid silver",
                          }}
                          className={`${darkMode && "border border-white "}`}
                        />
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
      </div>
      {openConfirmation && (
        <Dialog
          open={openConfirmation}
          onClose={() => {
            setSelect(false);
            setSelectedFiles([]);
            setOpenConfirmation(false);
          }}
          PaperProps={{
            style: {
              borderRadius: "5px",
              maxWidth: "80%",
              width: "auto",
              maxHeight: "80%",
            },
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent
            style={{
              backgroundColor: "#FEF2F2",
            }}
          >
            <div className="my-2 p-3 flex flex-col justify-center items-center gap-6">
              <img src={Danger} alt="." />
              <h2 className="text-2xl font-semibold text-gray-700">
                Are you sure?
              </h2>
              <div className="text-center">
                <p>This action will delete the following files</p>
                <ul className="my-8">
                  {selectedFiles.map((file) => (
                    <li key={file.fileId || file.id} className="font-bold">
                      {file?.fileName}
                    </li>
                  ))}
                </ul>
                <p>You won't be able to revert this action!</p>
              </div>

              <div className="flex flex-row justify-center items-center gap-2">
                <button
                  className="px-4 py-1 rounded-lg shadow-sm bg-red-500 text-white"
                  onClick={deleteFiles}
                >
                  {delLoading ? "deleting..." : "Confirm"}
                </button>
                <button
                  className="px-4 py-1 mx-2 rounded-lg shadow-sm border border-gray-300"
                  onClick={() => {
                    setSelect(false);
                    setSelectedFiles([]);
                    setOpenConfirmation(false);
                  }}
                  color="primary"
                >
                  Cancel
                </button>
              </div>

              <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
              >
                <MuiAlert
                  elevation={6}
                  variant="filled"
                  onClose={handleSnackbarClose}
                  severity={snackbarSeverity}
                >
                  {snackbarMessage}
                </MuiAlert>
              </Snackbar>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default RecentDeletedFiles;
